/**
 * TeamDashboardPage — Centraal team dashboard voor projectmanager/partner/teamleider/agent.
 *
 * Functies (FASE 2+3):
 *  - Mijn trials (lijst + activate-knop)
 *  - Mijn kortingscodes (lijst + create-modal met promo-URL copy)
 *  - Mijn klanten (lijst + zoek + filter op trial/paid)
 *  - Mijn commissies (overzicht + uitbetaal-status)
 *  - Basis-statistieken
 *
 * Hiërarchie-respect: backend filtert alleen toegestane data.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Users, Ticket, Sparkles, Copy, Plus, X, RefreshCw, Loader2,
  CheckCircle2, Clock, XCircle, Check, Tag, Send, Mail, Coins, TrendingUp,
} from 'lucide-react';

const ROLE_LABELS = {
  admin: 'Admin', manager: 'Manager', specialist: 'Specialist',
  projectmanager: 'Projectmanager', partner: 'Partner', teamleider: 'Teamleider', agent: 'Agent',
};

const formatEUR = (n) => `€${(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


export default function TeamDashboardPage() {
  const { api, user } = useAuth();
  const [tab, setTab] = useState('trials');
  const [trials, setTrials] = useState([]);
  const [codes, setCodes] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [commissions, setCommissions] = useState({ commissions: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [showCreateTrial, setShowCreateTrial] = useState(false);
  const [showCreateCode, setShowCreateCode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c, k, com] = await Promise.all([
        api('get', '/api/team/trials'),
        api('get', '/api/team/discount-codes'),
        api('get', '/api/admin/team/users?role=klanten').catch(() => ({ data: { users: [] } })),
        api('get', '/api/team/commissions').catch(() => ({ data: { commissions: [], summary: {} } })),
      ]);
      setTrials(t.data.trials || []);
      setCodes(c.data.discount_codes || []);
      // klanten = users met klant-rollen onder mij
      const allUsers = k.data.users || [];
      setKlanten(allUsers.filter((u) => ['radar_free', 'radar_paid', 'beheer_paid'].includes(u.role)));
      setCommissions(com.data || { commissions: [], summary: {} });
    } catch (e) {
      toast.error('Fout bij laden');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const stats = {
    trials_active: trials.filter((t) => t.status === 'actief' || t.status === 'bijna_verlopen').length,
    trials_converted: trials.filter((t) => t.geconverteerd_naar_betaald).length,
    codes_total: codes.length,
    codes_used: codes.filter((c) => (c.current_usage || 0) > 0).length,
    klanten_total: klanten.length,
    klanten_paid: klanten.filter((k) => ['radar_paid', 'beheer_paid'].includes(k.role)).length,
    commissions_pending: commissions.summary?.total_pending_ex_vat || 0,
    commissions_paid: commissions.summary?.total_paid_ex_vat || 0,
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* ── Header ───────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[#D4A017] text-xs uppercase tracking-wider font-bold mb-1">
              {ROLE_LABELS[user?.role] || user?.role} dashboard
            </p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
              Welkom, {user?.name?.split(' ')[0] || 'team'}
            </h1>
            <p className="text-white/60 text-sm mt-1">Beheer uw klanten, trials, kortingscodes en commissies</p>
          </div>
          <button
            onClick={load}
            className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5"
            data-testid="team-refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* ── Stats ────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Mijn klanten" value={stats.klanten_total} icon={Users} color="text-cyan-400" />
          <StatCard label="Betalend" value={stats.klanten_paid} icon={CheckCircle2} color="text-emerald-400" />
          <StatCard label="Open commissie" value={formatEUR(stats.commissions_pending)} icon={Coins} color="text-amber-400" />
          <StatCard label="Uitbetaald" value={formatEUR(stats.commissions_paid)} icon={TrendingUp} color="text-[#D4A017]" />
        </div>

        {/* ── Tabs ─────────────────────────────────────────── */}
        <div className="flex items-center gap-2 mb-5 border-b border-white/10 flex-wrap">
          {[
            { id: 'klanten', label: `Mijn klanten (${stats.klanten_total})`, icon: Users },
            { id: 'trials', label: `Trials (${stats.trials_active})`, icon: Sparkles },
            { id: 'codes', label: `Codes (${stats.codes_total})`, icon: Ticket },
            { id: 'commissies', label: `Commissies (${commissions.commissions.length})`, icon: Coins },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`team-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.id ? 'border-[#D4A017] text-[#D4A017]' : 'border-transparent text-white/60 hover:text-white'
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
          {tab === 'trials' && (
            <button
              onClick={() => setShowCreateTrial(true)}
              className="ml-auto bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
              data-testid="open-create-trial"
            >
              <Plus size={14} /> 30-dagen trial activeren
            </button>
          )}
          {tab === 'codes' && (
            <button
              onClick={() => setShowCreateCode(true)}
              className="ml-auto bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
              data-testid="open-create-code"
            >
              <Plus size={14} /> Nieuwe kortingscode
            </button>
          )}
        </div>

        {/* ── Content ───────────────────────────────────────── */}
        {loading ? (
          <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : tab === 'trials' ? (
          <TrialsList trials={trials} api={api} onChange={load} />
        ) : tab === 'codes' ? (
          <CodesList codes={codes} />
        ) : tab === 'klanten' ? (
          <KlantenList klanten={klanten} />
        ) : (
          <CommissionsList commissions={commissions.commissions} />
        )}

        {showCreateTrial && (
          <CreateTrialModal api={api} onClose={() => setShowCreateTrial(false)} onCreated={() => { setShowCreateTrial(false); load(); }} />
        )}
        {showCreateCode && (
          <CreateCodeModal api={api} onClose={() => setShowCreateCode(false)} onCreated={() => { setShowCreateCode(false); load(); }} />
        )}
      </div>
    </div>
  );
}


function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/5">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={color} />
        <p className="text-white/50 text-xs uppercase tracking-wide font-semibold">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`} style={{ fontFamily: "'Sora', sans-serif" }}>{value}</p>
    </div>
  );
}


function TrialsList({ trials, api, onChange }) {
  if (trials.length === 0) {
    return <div className="bg-white/5 rounded-xl p-8 text-center text-white/40">Nog geen trials geactiveerd.</div>;
  }
  const handleConvert = async (id) => {
    if (!confirm('Markeer als geconverteerd naar betaald?')) return;
    try {
      await api('post', `/api/team/trials/${id}/convert`, { notes: 'Manueel geconverteerd' });
      toast.success('Trial geconverteerd');
      onChange();
    } catch { toast.error('Fout'); }
  };
  return (
    <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5">
          <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
            <th className="px-4 py-3">Klant</th>
            <th className="px-4 py-3">Start</th>
            <th className="px-4 py-3">Einde</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {trials.map((t) => (
            <tr key={t.id} data-testid={`trial-row-${t.id}`} className="hover:bg-white/5">
              <td className="px-4 py-3">
                <p className="text-white">{t.klant_name || t.klant_email}</p>
                <p className="text-white/40 text-xs font-mono">{t.klant_email}</p>
              </td>
              <td className="px-4 py-3 text-white/70 text-xs">{t.startdatum?.slice(0, 10)}</td>
              <td className="px-4 py-3 text-white/70 text-xs">{t.einddatum?.slice(0, 10)}</td>
              <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
              <td className="px-4 py-3 text-right">
                {(t.status === 'actief' || t.status === 'bijna_verlopen') && !t.geconverteerd_naar_betaald && (
                  <button onClick={() => handleConvert(t.id)} className="text-emerald-400 hover:text-emerald-300 text-xs font-semibold" data-testid={`convert-${t.id}`}>
                    → Geconverteerd
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function CodesList({ codes }) {
  const copyUrl = (url, code) => {
    navigator.clipboard.writeText(url);
    toast.success(`Promo-URL voor ${code} gekopieerd`);
  };
  if (codes.length === 0) {
    return <div className="bg-white/5 rounded-xl p-8 text-center text-white/40">Nog geen kortingscodes aangemaakt.</div>;
  }
  return (
    <div className="space-y-3">
      {codes.map((c) => (
        <div key={c.id} data-testid={`code-row-${c.id}`} className="bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <code className="bg-[#D4A017]/10 text-[#D4A017] px-2 py-1 rounded font-mono font-bold">{c.code}</code>
                {c.active ? (
                  <span className="text-emerald-400 text-xs">Actief</span>
                ) : (
                  <span className="text-red-400 text-xs">Inactief</span>
                )}
                <span className="text-white/40 text-xs">
                  {c.discount_type === 'percentage' ? `${c.discount_value}% korting` : `€${c.discount_value} korting`}
                </span>
                <span className="text-white/40 text-xs">· {c.valid_first_x_months} mnd</span>
              </div>
              <p className="text-white/60 text-xs">
                Klant: <span className="text-white">{c.gekoppelde_klant_email || '— (algemeen)'}</span> · Gebruikt: {c.current_usage}/{c.max_usage}
              </p>
              <p className="text-white/40 text-xs font-mono mt-1">{c.promo_url}</p>
            </div>
            <button
              onClick={() => copyUrl(c.promo_url, c.code)}
              className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5"
              data-testid={`copy-url-${c.id}`}
            >
              <Copy size={12} /> Kopieer promo-URL
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


function StatusBadge({ status }) {
  const map = {
    actief: { c: 'text-emerald-400 bg-emerald-500/10', icon: Clock, label: 'Actief' },
    bijna_verlopen: { c: 'text-amber-400 bg-amber-500/10', icon: Clock, label: 'Bijna verlopen' },
    verlopen: { c: 'text-red-400 bg-red-500/10', icon: XCircle, label: 'Verlopen' },
    geconverteerd: { c: 'text-blue-400 bg-blue-500/10', icon: CheckCircle2, label: 'Geconverteerd' },
    geannuleerd: { c: 'text-white/40 bg-white/5', icon: XCircle, label: 'Geannuleerd' },
  };
  const s = map[status] || map.actief;
  const Ic = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${s.c}`}>
      <Ic size={10} /> {s.label}
    </span>
  );
}


function KlantenList({ klanten }) {
  const [q, setQ] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const filtered = klanten.filter((k) => {
    if (q && !(k.email?.toLowerCase().includes(q.toLowerCase()) || k.name?.toLowerCase().includes(q.toLowerCase()))) return false;
    if (filter === 'paid') return ['radar_paid', 'beheer_paid'].includes(k.role);
    if (filter === 'free') return k.role === 'radar_free';
    if (filter === 'managed') return k.role === 'beheer_paid';
    return true;
  });
  const roleLabel = (r) => ({ radar_free: 'Free', radar_paid: 'Self-Service', beheer_paid: 'Managed AI' }[r] || r);
  const roleColor = (r) => ({ radar_free: 'text-white/40 bg-white/5', radar_paid: 'text-cyan-400 bg-cyan-500/10', beheer_paid: 'text-[#D4A017] bg-[#D4A017]/10' }[r] || 'text-white/40 bg-white/5');

  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam of e-mail" className="flex-1 min-w-[200px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/40" data-testid="klanten-search" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="klanten-filter">
          <option value="">Alle</option>
          <option value="paid">Betalend</option>
          <option value="free">Gratis</option>
          <option value="managed">Managed AI</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white/5 rounded-xl p-8 text-center text-white/40">Geen klanten gevonden.</div>
      ) : (
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Klant</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Aangemaakt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((k) => (
                <tr key={k.id} className="hover:bg-white/5" data-testid={`klant-row-${k.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-white">{k.name || k.email}</p>
                    <p className="text-white/40 text-xs">{k.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${roleColor(k.role)}`}>{roleLabel(k.role)}</span>
                  </td>
                  <td className="px-4 py-3 text-white/50 text-xs">{k.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function CommissionsList({ commissions }) {
  if (!commissions || commissions.length === 0) {
    return <div className="bg-white/5 rounded-xl p-8 text-center text-white/40" data-testid="no-commissions">Nog geen commissies. Commissies worden automatisch berekend zodra uw klanten betalen.</div>;
  }
  const kindLabel = (k) => ({ partner_fixed: 'Callcenter (vast €495,95)', affiliate_percentage: 'Affiliate (20%)' }[k] || k);
  return (
    <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/5">
          <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
            <th className="px-4 py-3">Klant</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3 text-right">Bedrag</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Datum</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {commissions.map((c) => (
            <tr key={c.id} className="hover:bg-white/5" data-testid={`commission-${c.id}`}>
              <td className="px-4 py-3">
                <p className="text-white">{c.klant_name || c.klant_email}</p>
              </td>
              <td className="px-4 py-3 text-xs text-white/70">{kindLabel(c.kind)}</td>
              <td className="px-4 py-3 text-right text-white font-bold">{formatEUR(c.amount_ex_vat)}</td>
              <td className="px-4 py-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'paid' ? 'text-emerald-400 bg-emerald-500/10' : c.status === 'cancelled' ? 'text-red-400 bg-red-500/10' : 'text-amber-400 bg-amber-500/10'}`}>{c.status === 'paid' ? 'Uitbetaald' : c.status === 'cancelled' ? 'Geannuleerd' : 'Open'}</span>
              </td>
              <td className="px-4 py-3 text-white/50 text-xs">{c.created_at?.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function CreateTrialModal({ api, onClose, onCreated }) {
  const [mode, setMode] = useState('new');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [klantUserId, setKlantUserId] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = mode === 'new'
        ? { klant_email: email, klant_name: name, klant_phone: phone, klant_company: company }
        : { klant_user_id: klantUserId };
      await api('post', '/api/team/trials', body);
      toast.success('30-dagen trial actief — klant krijgt mail');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fout bij activeren');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="create-trial-modal">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>30-dagen Self-Service trial</h2>
          <button onClick={onClose}><X size={18} className="text-white/40 hover:text-white" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          {['new', 'existing'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold ${
                mode === m ? 'bg-[#D4A017] text-[#0f172a]' : 'bg-white/5 text-white/60 border border-white/10'
              }`}
            >
              {m === 'new' ? 'Nieuwe klant' : 'Bestaande klant ID'}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === 'new' ? (
            <>
              <div>
                <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">E-mail klant *</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="trial-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Naam *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="trial-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefoon" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
                <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Bedrijf" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
              </div>
            </>
          ) : (
            <div>
              <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Klant user_id *</label>
              <input value={klantUserId} onChange={(e) => setKlantUserId(e.target.value)} required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-xs" placeholder="uuid van klant" data-testid="trial-klant-id" />
            </div>
          )}
          <button type="submit" disabled={saving} className="w-full bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 text-[#0f172a] font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 mt-2" data-testid="submit-trial">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Activeren…</> : <><Send size={14} /> Activeer 30-dagen trial</>}
          </button>
          <p className="text-xs text-white/40 text-center">Klant ontvangt direct een welkomstmail. Trial loopt automatisch af na 30 dagen.</p>
        </form>
      </div>
    </div>
  );
}


function CreateCodeModal({ api, onClose, onCreated }) {
  const [klantEmail, setKlantEmail] = useState('');
  const [type, setType] = useState('percentage');
  const [value, setValue] = useState(20);
  const [months, setMonths] = useState(3);
  const [pkg, setPkg] = useState('');
  const [prefix, setPrefix] = useState('');
  const [maxUsage, setMaxUsage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Klant ophalen via e-mail als ingevuld
      let klantId = null;
      if (klantEmail) {
        try {
          const search = await api('get', `/api/admin/team/users?search=${encodeURIComponent(klantEmail)}`);
          const found = (search.data.users || []).find((u) => u.email?.toLowerCase() === klantEmail.toLowerCase());
          if (found) klantId = found.id;
        } catch { /* skip */ }
      }
      const r = await api('post', '/api/team/discount-codes', {
        klant_user_id: klantId,
        code_prefix: prefix || undefined,
        discount_type: type,
        discount_value: Number(value),
        valid_first_x_months: Number(months),
        valid_for_package: pkg || null,
        max_usage: Number(maxUsage),
      });
      setResult(r.data.discount_code);
      toast.success(`Kortingscode ${r.data.discount_code.code} aangemaakt`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aanmaken mislukt');
    }
    setSaving(false);
  };

  const copyAll = () => {
    navigator.clipboard.writeText(result.promo_url);
    toast.success('Promo-URL gekopieerd!');
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="create-code-modal">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Nieuwe kortingscode</h2>
          <button onClick={onClose}><X size={18} className="text-white/40 hover:text-white" /></button>
        </div>

        {result ? (
          <div data-testid="code-created-result">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 mb-4">
              <p className="text-emerald-300 font-bold mb-2 flex items-center gap-2"><CheckCircle2 size={18} /> Code aangemaakt</p>
              <p className="text-3xl font-mono text-emerald-400 mb-3">{result.code}</p>
              <p className="text-white/70 text-sm break-all bg-black/30 p-2 rounded font-mono">{result.promo_url}</p>
            </div>
            <button onClick={copyAll} className="w-full bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 mb-2" data-testid="copy-promo-url">
              <Copy size={14} /> Kopieer promo-URL
            </button>
            <button onClick={onCreated} className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white py-2 rounded-xl text-sm">
              Sluiten
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Klant e-mail (optioneel)</label>
              <input value={klantEmail} onChange={(e) => setKlantEmail(e.target.value)} placeholder="klant@bedrijf.nl" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="code-klant-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Type</label>
                <select value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="code-type">
                  <option value="percentage">Percentage</option>
                  <option value="vast_bedrag">Vast bedrag €</option>
                  <option value="vaste_prijs">Vaste prijs € (override)</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">
                  {type === 'percentage' ? 'Korting %' : type === 'vaste_prijs' ? 'Nieuwe prijs ex btw' : 'Korting €'} *
                </label>
                <input value={value} onChange={(e) => setValue(e.target.value)} type="number" min={0.01} step="0.01" required className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="code-value" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Eerste X maanden</label>
                <input value={months} onChange={(e) => setMonths(e.target.value)} type="number" min={1} max={12} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Max gebruik</label>
                <input value={maxUsage} onChange={(e) => setMaxUsage(e.target.value)} type="number" min={1} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Geldig voor pakket (optioneel)</label>
              <select value={pkg} onChange={(e) => setPkg(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white">
                <option value="">Alle pakketten</option>
                <option value="radar_monthly">Self-Service (€14,95)</option>
                <option value="imperium_beheer_launch">Managed AI launch (€995,95)</option>
                <option value="imperium_beheer">Managed AI recurring (€2.495,95)</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-white/50 font-semibold block mb-1">Prefix code (optioneel)</label>
              <input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase().slice(0, 4))} placeholder="bv. ANNA" maxLength={4} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            </div>
            <button type="submit" disabled={saving} className="w-full bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 text-[#0f172a] font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 mt-2" data-testid="submit-code">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Aanmaken…</> : <><Check size={14} /> Code aanmaken</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
